<template>

    <div class="ui-popup" :class="classes" :style="style" v-spinner="loading" @transitionend="onTransitionEnd">

        <div class="pointer" v-if="pointer"></div>

        <div class="content-holder" :style="contentStyle">
            <div class="popup-content" ref="contentRef">
                <slot v-if="!hide"></slot>
            </div>
        </div>

    </div>

</template>

<script lang="ts">

import { Component, Prop, Ref, Vue } from 'vue-facing-decorator';

import { isObject } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

import spinnerDirective from '../../directive/spinner';
import type { ResolvedPopupPlacement } from '../overlay';

@Component({
    name: 'ui-popup',
    directives: {
        spinner: spinnerDirective
    }
})
export default class Popup extends Vue {

    @Prop({ type: Object })
    placement: ResolvedPopupPlacement;

    @Prop({ type: Number, default: 400 })
    maxHeight: number;

    @Prop({ type: Boolean, default: false })
    pointer: boolean;

    @Prop({ type: [Boolean, Number, Object] })
    loading: boolean | number | object;

    @Prop({ type: [Boolean, Number] })
    hide: boolean | number;

    @Prop({ type: Boolean, default: false })
    animate: boolean;

    @Ref
    contentRef: HTMLDivElement;

    contentSize = null;

    animateEnabled = false;
    animating = false;

    paddingValue: number;
    contentObserver: ResizeObserver;

    mounted() {

        if(this.animate){

            const paddingString = window.getComputedStyle(this.$el, null).getPropertyValue('padding-left');
            this.paddingValue = parseInt(paddingString.replace('px', ''));

            this.observeContentSize();

            this.$nextTick(() => {

                this.updatePopupSize(this.contentRef.getBoundingClientRect());

                this.$nextTick(() => {
                    this.animateEnabled = true;
                });
            });
        }
    }

    beforeUnmount() {

        if(this.contentObserver){
            this.contentObserver.disconnect();
        }
    }

    get classes() {

        const classes: string[] = this.placement ? [this.placement.side, this.placement.position] : [];

        if(this.pointer){
            classes.push('has-pointer');
        }

        if(this.animateEnabled){
            classes.push('animate');
        }

        if(this.animating){
            classes.push('animating');
        }

        return classes;
    }

    get style() {

        const style: any = {};
        const contentSize = this.contentSize;

        if(this.animate && contentSize){

            style.width = contentSize.width + 'px';
            style.height = contentSize.height + 'px';
        }

        if(this.animate && this.maxHeight){
            style.maxHeight = this.maxHeight + 'px';
        }

        return style;
    }

    get contentStyle() {

        let style: any = {};

        if(!this.animate && this.maxHeight){
            style.maxHeight = this.maxHeight + 'px';
        }

        return style;
    }

    get isLoading(): boolean {

        return isObject(this.loading) ? (this.loading as any).loading : this.loading;
    }

    observeContentSize() {

        const $content = this.contentRef;

        this.contentObserver = new ResizeObserver(entries => {

            if(this.isLoading || !this.animateEnabled || this.animating){
                return;
            }

            for(const entry of entries){

                if(entry.target === $content){

                    const rect = entry.contentRect;
                    this.updatePopupSize(rect);
                    break;
                }
            }
        });

        this.contentObserver.observe($content);
    }

    updatePopupSize(rect) {

        const holderRect = this.contentRef.getBoundingClientRect();

        let width = rect.width;
        let height = rect.height;

        // Height > than container, so max width is enabled and scrollbar is visible: ignore scrollbar
        if(this.maxHeight && rect.height > holderRect.height){
            width = holderRect.width;
        }

        const newSize = {
            width: width + 2,
            height: height + 2
        };

        this.enableAnimatingIfNeeded(this.contentSize, newSize);

        this.contentSize = newSize;
    }

    enableAnimatingIfNeeded(oldSize, newSize) {

        // Skip when animate is disabled
        if(!this.animateEnabled){
            return;
        }

        // Skip when this is initial size
        if(!oldSize){
            return;
        }

        // Skip when size hasn't changed
        if(newSize.width === oldSize.width && newSize.height === oldSize.height){
            return;
        }

        // Skip when size animation is not visible because of max height
        if(this.maxHeight && oldSize.height > this.maxHeight && newSize.height > this.maxHeight){
            return;
        }

        this.animating = true;
    }

    onTransitionEnd(e: TransitionEvent) {

        if(e.target === this.$el && (e.propertyName === 'height' || e.propertyName === 'width')){
            this.animating = false;
        }
    }
}

export type PopupComp = InstanceType<typeof Popup>
export type PopupProps = ComponentProps<PopupComp>
export const popupCompMixin = createComponentMixin(Popup)

</script>
<style src="../../../scss/component/popup/Popup.scss" lang="scss"></style>